import * as yup from 'yup'
import { Checkbox } from '@/components/checkbox'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import Select from '@/components/select'
import StatusBadge from '@/components/status-badge'
import { Textarea } from '@/components/textarea'
import {
  REGIME_ATENDIMENTO_OPTIONS,
  TIPO_ACIDENTE_OPTIONS,
  TIPO_CONSULTA_OPTIONS,
} from '@/types/domain'
import { comCondutaPadrao } from '@/types/credencial'
import type { Credencial } from '@/types/credencial'
import CredencialForm from '../credencial-form'
import styles from '../style.module.css'

const schema = yup.object({
  hasBiometrics: yup.boolean().required(),
  hasExecution: yup.boolean().required(),
  acceptJustification: yup.boolean().required(),
  solicitaTokenPreAutorizacao: yup.boolean().required(),
  requerTokenAutorizacao: yup.boolean().required(),
  minimoAnexosPreAutorizacao: yup
    .number()
    .typeError('Informe um número')
    .min(0, 'Não pode ser negativo')
    .integer('Use um número inteiro')
    .required('Preencha o mínimo de anexos'),
  indicacaoClinicaTexto: yup.string().defined().nullable().default(''),
  indicacaoClinicaMin: yup
    .number()
    .typeError('Informe um número')
    .min(0, 'Não pode ser negativo')
    .integer('Use um número inteiro')
    .required('Preencha o mínimo'),
  indicacaoClinicaMax: yup
    .number()
    .typeError('Informe um número')
    .min(0, 'Não pode ser negativo')
    .integer('Use um número inteiro')
    .required('Preencha o máximo')
    .test('maior-que-minimo', 'O máximo não pode ser menor que o mínimo', function (valor) {
      const { indicacaoClinicaMin } = this.parent as { indicacaoClinicaMin?: number }
      if (
        valor === undefined ||
        valor === null ||
        indicacaoClinicaMin === undefined ||
        indicacaoClinicaMin === null
      )
        return true
      return valor >= indicacaoClinicaMin
    }),
  regimeAtendimento: yup.string().defined().nullable().default(''),
  tipoAcidente: yup.string().defined().nullable().default(''),
  tipoConsulta: yup.string().defined().nullable().default(''),
})

const AutorizacaoTab = ({ credencial }: { credencial: Credencial }) => (
  <CredencialForm
    credencial={credencial}
    schema={schema}
    defaultValues={{
      hasBiometrics: credencial.hasBiometrics,
      hasExecution: credencial.hasExecution,
      acceptJustification: credencial.acceptJustification,
      solicitaTokenPreAutorizacao: credencial.solicitaTokenPreAutorizacao,
      requerTokenAutorizacao: credencial.requerTokenAutorizacao,
      minimoAnexosPreAutorizacao: credencial.minimoAnexosPreAutorizacao,
      indicacaoClinicaTexto: credencial.indicacaoClinicaTexto ?? '',
      indicacaoClinicaMin: credencial.indicacaoClinicaMin,
      indicacaoClinicaMax: credencial.indicacaoClinicaMax,
      regimeAtendimento: comCondutaPadrao(credencial).regimeAtendimento ?? '',
      tipoAcidente: comCondutaPadrao(credencial).tipoAcidente ?? '',
      tipoConsulta: credencial.tipoConsulta ?? '',
    }}
    montarPayload={(dados) => ({
      ...credencial,
      hasBiometrics: dados.hasBiometrics,
      hasExecution: dados.hasExecution,
      acceptJustification: dados.acceptJustification,
      solicitaTokenPreAutorizacao: dados.solicitaTokenPreAutorizacao,
      requerTokenAutorizacao: dados.requerTokenAutorizacao,
      minimoAnexosPreAutorizacao: dados.minimoAnexosPreAutorizacao,
      indicacaoClinicaTexto: dados.indicacaoClinicaTexto || null,
      indicacaoClinicaMin: dados.indicacaoClinicaMin,
      indicacaoClinicaMax: dados.indicacaoClinicaMax,
      regimeAtendimento: dados.regimeAtendimento || null,
      tipoAcidente: dados.tipoAcidente || null,
      tipoConsulta: dados.tipoConsulta || null,
    })}
  >
    {({ formState: { errors }, register, watch }, podeEditar) => {
      const minimoAtual = watch('minimoAnexosPreAutorizacao')
      const exigeAnexo = Number(minimoAtual) > 0

      return (
        <>
          <div className={styles.grid}>
            <Checkbox
              id="hasBiometrics"
              label="Exige biometria"
              disabled={!podeEditar}
              {...register('hasBiometrics')}
            />
            <Checkbox
              id="hasExecution"
              label="Tem execução"
              disabled={!podeEditar}
              {...register('hasExecution')}
            />
            <Checkbox
              id="acceptJustification"
              label="Aceita justificativa"
              disabled={!podeEditar}
              {...register('acceptJustification')}
            />
            <Checkbox
              id="solicitaTokenPreAutorizacao"
              label="Solicita token na pré-autorização"
              disabled={!podeEditar}
              {...register('solicitaTokenPreAutorizacao')}
            />
            <Checkbox
              id="requerTokenAutorizacao"
              label="Exige token na autorização"
              disabled={!podeEditar}
              {...register('requerTokenAutorizacao')}
            />
          </div>

          <div className={styles.grid}>
            <FormField
              label="Mínimo de anexos para autorizar"
              htmlFor="minimoAnexosPreAutorizacao"
              required
              error={errors.minimoAnexosPreAutorizacao?.message}
              hint="Quantos anexos o cliente precisa enviar antes de pedir autorização por essa credencial."
            >
              <Input
                id="minimoAnexosPreAutorizacao"
                type="number"
                min={0}
                disabled={!podeEditar}
                invalid={Boolean(errors.minimoAnexosPreAutorizacao)}
                {...register('minimoAnexosPreAutorizacao', { valueAsNumber: true })}
              />
            </FormField>

            <FormField
              label="Exige anexo para autorizar"
              hint="Calculado sozinho a partir do mínimo de anexos, ao lado."
            >
              <StatusBadge
                label={exigeAnexo ? 'Sim' : 'Não'}
                tone={exigeAnexo ? 'success' : 'neutral'}
              />
            </FormField>

            <FormField
              label="Procedimentos por guia"
              hint="Vem do tipo de credencial, não pode ser alterado por aqui."
            >
              <span>
                {credencial.procedimentosPorGuia > 0
                  ? credencial.procedimentosPorGuia
                  : 'Sem limite definido'}
              </span>
            </FormField>
          </div>

          <FormField
            label="Texto padrão da indicação clínica"
            htmlFor="indicacaoClinicaTexto"
            hint="Preenchido automaticamente ao abrir uma autorização por essa credencial."
          >
            <Textarea
              id="indicacaoClinicaTexto"
              disabled={!podeEditar}
              {...register('indicacaoClinicaTexto')}
            />
          </FormField>

          <div className={styles.grid}>
            <FormField
              label="Tamanho mínimo da indicação clínica"
              htmlFor="indicacaoClinicaMin"
              required
              error={errors.indicacaoClinicaMin?.message}
            >
              <Input
                id="indicacaoClinicaMin"
                type="number"
                min={0}
                disabled={!podeEditar}
                invalid={Boolean(errors.indicacaoClinicaMin)}
                {...register('indicacaoClinicaMin', { valueAsNumber: true })}
              />
            </FormField>

            <FormField
              label="Tamanho máximo da indicação clínica"
              htmlFor="indicacaoClinicaMax"
              required
              error={errors.indicacaoClinicaMax?.message}
            >
              <Input
                id="indicacaoClinicaMax"
                type="number"
                min={0}
                disabled={!podeEditar}
                invalid={Boolean(errors.indicacaoClinicaMax)}
                {...register('indicacaoClinicaMax', { valueAsNumber: true })}
              />
            </FormField>

            <FormField label="Regime de atendimento" htmlFor="regimeAtendimento">
              <Select
                id="regimeAtendimento"
                options={REGIME_ATENDIMENTO_OPTIONS}
                disabled={!podeEditar}
                {...register('regimeAtendimento')}
              />
            </FormField>

            <FormField label="Tipo de acidente" htmlFor="tipoAcidente">
              <Select
                id="tipoAcidente"
                options={TIPO_ACIDENTE_OPTIONS}
                disabled={!podeEditar}
                {...register('tipoAcidente')}
              />
            </FormField>

            <FormField label="Tipo de consulta" htmlFor="tipoConsulta">
              <Select
                id="tipoConsulta"
                options={TIPO_CONSULTA_OPTIONS}
                disabled={!podeEditar}
                {...register('tipoConsulta')}
              />
            </FormField>
          </div>
        </>
      )
    }}
  </CredencialForm>
)

export default AutorizacaoTab
